import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, ShieldCheck, TrendingDown, BarChart3, Zap, Target, Users, Wine, ShoppingCart, Factory, Truck, Globe2, FileCheck } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const IMAGES = {
  // Imagem diurna de operação portuária internacional (container terminal)
  hero: 'https://images.pexels.com/photos/3057960/pexels-photo-3057960.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1500&w=1200',
  containers: 'https://images.pexels.com/photos/1427107/pexels-photo-1427107.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  portDay: 'https://images.pexels.com/photos/1427541/pexels-photo-1427541.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  wine: 'https://images.unsplash.com/photo-1633119985201-7fb2c6eb1aec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHw0fHxwcmVtaXVtJTIwd2luZSUyMGJvdHRsZXMlMjBjZWxsYXIlMjBkYXJrfGVufDB8fHx8MTc3NzM0MzYzMXww&ixlib=rb-4.1.0&q=85',
  // Alimentos: fotografia real de setor alimentício (frutas/produtos importados)
  food: 'https://images.pexels.com/photos/1300972/pexels-photo-1300972.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  supermarket: 'https://images.unsplash.com/photo-1760463921690-9bec0cc7d434?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHw0fHxzdXBlcm1hcmtldCUyMGdyb2NlcnklMjBzdG9yZSUyMHJldGFpbCUyMHNoZWx2ZXMlMjBhaXNsZXxlbnwwfHx8fDE3NzczNDM2NDF8MA&ixlib=rb-4.1.0&q=85',
  warehouse: 'https://images.pexels.com/photos/4487363/pexels-photo-4487363.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  // Indústria: fábrica com máquinas (diferencia visualmente de "Importadores e parceiros comerciais")
  industry: 'https://images.pexels.com/photos/1108101/pexels-photo-1108101.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  // Importadores e parceiros: escritório corporativo / handshake B2B
  partners: 'https://images.pexels.com/photos/3184418/pexels-photo-3184418.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
};

/* ---------- HERO — light, 2 colunas, altura reduzida ---------- */
const HeroSection = ({ t }) => (
  <section className="relative bg-white pt-28 md:pt-32 pb-14 md:pb-20">
    <div className="cv-container">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
          className="lg:col-span-7">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-brass" aria-hidden="true"></span>
            <span className="text-[11px] font-semibold uppercase tracking-[0.28em] text-abyssal">
              {t('COSTA VALENTE TRADING', 'COSTA VALENTE TRADING')}
            </span>
          </div>
          <h1 className="text-[40px] sm:text-5xl lg:text-[56px] tracking-tight font-bold text-abyssal leading-[1.05] mb-6">
            {t('Soluções completas em ', 'Complete solutions in ')}
            <span className="text-cobalt">{t('comércio exterior.', 'international trade.')}</span>
          </h1>          <p className="text-lg md:text-xl text-abyssal font-medium leading-relaxed mb-5 max-w-2xl">
            {t(
              'Importe com segurança, estratégia e eficiência.',
              'Import with security, strategy, and efficiency.'
            )}
          </p>
          <p className="text-base md:text-lg text-steel leading-relaxed mb-9 max-w-2xl">
            {t(
              'Planejamos e conduzimos cada etapa da importação, integrando estratégia tributária, conformidade, logística e desembaraço para reduzir riscos e dar previsibilidade à operação.',
              'We plan and conduct every step of the import process, integrating tax strategy, compliance, logistics and clearance to reduce risks and deliver operational predictability.'
            )}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <a href="https://wa.me/5527996482098" target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-cta"
              className="inline-flex items-center justify-center gap-2 bg-abyssal hover:bg-cobalt text-white px-7 py-3.5 rounded-md font-semibold text-base transition-colors">
              <Phone className="h-5 w-5" />
              {t('Falar com especialista', 'Talk to specialist')}
            </a>
            <Link to="/servicos" data-testid="hero-services-cta"
              className="inline-flex items-center justify-center gap-2 bg-white border border-abyssal text-abyssal px-7 py-3.5 rounded-md font-semibold text-base hover:bg-mist transition-colors">
              {t('Nossos serviços', 'Our services')}
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8 }}
          className="lg:col-span-5">
          <div className="relative overflow-hidden rounded-md aspect-[4/5] lg:aspect-[3/4]">
            <picture>
              <source
                media="(max-width: 767px)"
                type="image/webp"
                srcSet="/media/hero-ship-mobile.webp"
              />
              <source
                type="image/webp"
                srcSet="/media/hero-ship.webp"
              />
              <img
                src="/media/hero-ship.jpg"
                alt={t('Navio porta-contêineres em rota internacional', 'Container ship on international route')}
                width="1400"
                height="1867"
                loading="eager"
                className="w-full h-full object-cover object-center"
              />
            </picture>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

/* ---------- PILARES ---------- */
const PillarsSection = ({ t }) => {
  const pillars = [
    { id: 'profundidade', numeral: '01', titlePt: 'Profundidade', titleEn: 'Depth',
      descPt: 'Conhecimento de mercado, domínio técnico e expertise consolidada em comércio exterior.',
      descEn: 'Market knowledge, technical mastery, and consolidated expertise in international trade.' },
    { id: 'conexao', numeral: '02', titlePt: 'Conexão', titleEn: 'Connection',
      descPt: 'Conexão estratégica entre mercados, negócios e fronteiras internacionais.',
      descEn: 'Strategic connection between markets, businesses, and international borders.' },
    { id: 'solidez', numeral: '03', titlePt: 'Solidez', titleEn: 'Solidity',
      descPt: 'Credibilidade, confiança e transparência em cada operação conduzida.',
      descEn: 'Credibility, trust, and transparency in every operation conducted.' },
  ];
  return (
    <section className="bg-mist py-24 md:py-28" data-testid="pillars-section">
      <div className="cv-container">
        <div className="max-w-2xl mb-14">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-8 bg-brass"></span>
            <span className="text-[11px] font-semibold uppercase tracking-[0.28em] text-abyssal">
              {t('NOSSOS PILARES', 'OUR PILLARS')}
            </span>
          </div>
          <h2 className="text-[32px] sm:text-4xl lg:text-[44px] tracking-tight font-bold text-abyssal">
            {t('Os pilares que orientam cada operação.', 'The pillars that guide every operation.')}
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {pillars.map((p, idx) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.45, delay: idx * 0.08 }}
              className="bg-white border border-[#E6E6ED] rounded-md p-8 md:p-9" data-testid={`pillar-${p.id}`}>
              <span className="text-2xl font-bold text-cobalt block mb-6">{p.numeral}</span>
              <h3 className="text-2xl md:text-[28px] font-bold text-abyssal mb-3 tracking-tight">
                {t(p.titlePt, p.titleEn)}
              </h3>
              <p className="text-base text-steel leading-relaxed">{t(p.descPt, p.descEn)}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- QUEM SOMOS — bloco "Como atuamos" ao invés de Missão/Visão ---------- */
const AboutSection = ({ t }) => (
  <section className="bg-white py-24 md:py-28 border-t border-[#E6E6ED]">
    <div className="cv-container">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
        <motion.div initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
          className="lg:col-span-7">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-8 bg-brass"></span>
            <span className="text-[11px] font-semibold uppercase tracking-[0.28em] text-abyssal">
              {t('QUEM SOMOS', 'ABOUT US')}
            </span>
          </div>
          <h2 className="text-[32px] sm:text-4xl lg:text-[44px] tracking-tight font-bold text-abyssal mb-6 leading-[1.1]">
            {t('Estratégia e execução em cada etapa da importação.', 'Strategy and execution at every import stage.')}
          </h2>
          <p className="text-lg text-abyssal font-medium leading-relaxed mb-5">
            {t(
              'Somos uma trading company especializada em estruturar e conduzir operações de importação para empresas brasileiras. Integramos planejamento tributário, gestão documental, logística internacional, desembaraço e acompanhamento até a entrega.',
              'We are a trading company specialized in structuring and conducting import operations for Brazilian companies. We integrate tax planning, document management, international logistics, customs clearance and follow-up through to delivery.'
            )}
          </p>
          <p className="text-base text-steel leading-relaxed mb-8">
            {t(
              'Mais do que executar etapas, antecipamos riscos, coordenamos os diferentes intervenientes e mantemos o cliente informado ao longo de toda a operação.',
              'Beyond executing steps, we anticipate risks, coordinate the different stakeholders, and keep the client informed throughout the entire operation.'
            )}
          </p>
          <Link to="/sobre" data-testid="about-cta"
            className="inline-flex items-center gap-2 text-cobalt hover:text-abyssal font-semibold transition-colors underline underline-offset-4 decoration-cobalt/40 hover:decoration-abyssal">
            {t('Conheça a Costa Valente', 'Discover Costa Valente')}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>
        <motion.div initial={{ opacity: 0, x: 16 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
          className="lg:col-span-5">
          <div className="bg-mist border border-[#E6E6ED] rounded-md p-8 md:p-10">
            <div className="text-[11px] font-semibold uppercase tracking-[0.28em] text-abyssal mb-6">
              {t('COMO ATUAMOS', 'HOW WE OPERATE')}
            </div>
            <div className="space-y-6">
              {[
                { pt: 'Planejamento integrado', en: 'Integrated planning',
                  descPt: 'Análise tributária, logística e documental antes do início da operação.',
                  descEn: 'Tax, logistics and document analysis before starting the operation.' },
                { pt: 'Coordenação de ponta a ponta', en: 'End-to-end coordination',
                  descPt: 'Articulação entre fornecedor, transporte, desembaraço e destino final.',
                  descEn: 'Coordination between supplier, transport, clearance and final destination.' },
                { pt: 'Acompanhamento próximo', en: 'Close follow-up',
                  descPt: 'Status regular e canal direto com quem conduz sua operação.',
                  descEn: 'Regular updates and direct channel with the person handling your operation.' },
              ].map((item, i) => (
                <div key={item.en} className="flex items-start gap-4" data-testid={`how-item-${i}`}>
                  <span className="text-cobalt font-bold text-sm tabular-nums min-w-[24px]">{`0${i+1}`}</span>
                  <div>
                    <h3 className="text-base font-semibold text-abyssal mb-1">{t(item.pt, item.en)}</h3>
                    <p className="text-sm text-steel leading-relaxed">{t(item.descPt, item.descEn)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

/* ---------- SERVIÇOS RESUMO ---------- */
const ServicesSection = ({ t }) => {
  const services = [
    { id: 'conta-ordem', icon: FileCheck, titlePt: 'Importação por Conta e Ordem', titleEn: 'Import on Behalf',
      descPt: 'Realizamos todo o processo de importação em nome do cliente, com segurança jurídica e operacional completa.',
      descEn: 'We handle the entire import process on behalf of the client, with complete legal and operational security.' },
    { id: 'encomenda', icon: Globe2, titlePt: 'Importação por Encomenda', titleEn: 'Import by Order',
      descPt: 'Adquirimos a mercadoria no exterior com solução estratégica para simplificar sua operação.',
      descEn: 'We purchase goods abroad as a strategic solution to simplify your operation.' },
    { id: 'assessoria', icon: Target, titlePt: 'Assessoria Completa', titleEn: 'Full Consulting',
      descPt: 'Planejamento, classificação fiscal, logística, desembaraço e gestão de riscos de ponta a ponta.',
      descEn: 'Planning, tax classification, logistics, customs clearance and end-to-end risk management.' },
  ];
  return (
    <section className="bg-white py-24 md:py-28 border-t border-[#E6E6ED]">
      <div className="cv-container">
        <div className="max-w-2xl mb-14">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-8 bg-brass"></span>
            <span className="text-[11px] font-semibold uppercase tracking-[0.28em] text-abyssal">
              {t('SERVIÇOS', 'SERVICES')}
            </span>
          </div>
          <h2 className="text-[32px] sm:text-4xl lg:text-[44px] tracking-tight font-bold text-abyssal">
            {t('Soluções para cada modelo de importação.', 'Solutions for every import model.')}
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {services.map((s) => (
            <motion.div key={s.id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="bg-white border border-[#E6E6ED] hover:border-cobalt p-8 rounded-md transition-colors"
              data-testid={`home-service-${s.id}`}>
              <s.icon className="h-9 w-9 text-cobalt mb-6" strokeWidth={1.5} />
              <h3 className="text-xl font-bold text-abyssal mb-3">{t(s.titlePt, s.titleEn)}</h3>
              <p className="text-base text-steel leading-relaxed">{t(s.descPt, s.descEn)}</p>
            </motion.div>
          ))}
        </div>
        <div className="mt-12">
          <Link to="/servicos" data-testid="services-see-all"
            className="inline-flex items-center gap-2 bg-abyssal hover:bg-cobalt text-white px-7 py-3.5 rounded-md font-semibold transition-colors">
            {t('Ver todos os serviços', 'View all services')}
            <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

/* ---------- SETORES ---------- */
const SectorsSection = ({ t }) => {
  const sectors = [
    { id: 'wines', icon: Wine, titlePt: 'Vinhos e bebidas', titleEn: 'Wines & Beverages', img: IMAGES.wine },
    { id: 'food', icon: ShoppingCart, titlePt: 'Alimentos', titleEn: 'Food', img: IMAGES.food },
    { id: 'retail', icon: ShoppingCart, titlePt: 'Supermercados e redes de varejo', titleEn: 'Supermarkets and retail chains', img: IMAGES.supermarket },
    { id: 'logistics', icon: Truck, titlePt: 'Distribuidores e atacadistas', titleEn: 'Distributors & Wholesalers', img: IMAGES.warehouse },
    { id: 'industry', icon: Factory, titlePt: 'Indústrias', titleEn: 'Industries', img: IMAGES.industry },
    { id: 'trading', icon: Globe2, titlePt: 'Importadores e parceiros comerciais', titleEn: 'Importers and commercial partners', img: IMAGES.partners },
  ];
  return (
    <section className="bg-mist py-24 md:py-28">
      <div className="cv-container">
        <div className="max-w-2xl mb-14">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-8 bg-brass"></span>
            <span className="text-[11px] font-semibold uppercase tracking-[0.28em] text-abyssal">
              {t('SETORES ATENDIDOS', 'SECTORS WE SERVE')}
            </span>
          </div>
          <h2 className="text-[32px] sm:text-4xl lg:text-[44px] tracking-tight font-bold text-abyssal">
            {t('Experiência em diferentes segmentos.', 'Experience across different segments.')}
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {sectors.map((s) => (
            <motion.div key={s.id} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
              className="relative overflow-hidden rounded-md group aspect-[4/3]" data-testid={`sector-${s.id}`}>
              <img src={s.img} alt={t(s.titlePt, s.titleEn)}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-abyssal/85 via-abyssal/30 to-transparent"></div>
              <div className="absolute bottom-0 left-0 p-5">
                <s.icon className="h-5 w-5 text-white mb-2" strokeWidth={1.75} />
                <h3 className="text-base md:text-lg font-bold text-white leading-tight">{t(s.titlePt, s.titleEn)}</h3>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- DIFERENCIAIS ---------- */
const DifferentialsSection = ({ t }) => {
  const diffs = [
    { id: 'cost', icon: TrendingDown, titlePt: 'Otimização de custos', titleEn: 'Cost optimization', descPt: 'Estruturação tributária e fiscal com foco na redução de custos e no aproveitamento correto de regimes aplicáveis.', descEn: 'Tax and fiscal structuring focused on cost reduction and correct use of applicable regimes.' },
    { id: 'security', icon: ShieldCheck, titlePt: 'Conformidade fiscal e aduaneira', titleEn: 'Fiscal and customs compliance', descPt: 'Aderência às normas fiscais e aduaneiras aplicáveis em cada operação.', descEn: 'Adherence to applicable fiscal and customs rules in every operation.' },
    { id: 'intelligence', icon: BarChart3, titlePt: 'Inteligência tributária', titleEn: 'Tax intelligence', descPt: 'Aplicação de benefícios fiscais quando cabíveis à operação, como o INVEST IMPORT.', descEn: 'Application of tax benefits when applicable to the operation, such as INVEST IMPORT.' },
    { id: 'efficiency', icon: Zap, titlePt: 'Eficiência operacional', titleEn: 'Operational efficiency', descPt: 'Processos otimizados para velocidade e controle.', descEn: 'Optimized processes for speed and control.' },
    { id: 'strategy', icon: Target, titlePt: 'Planejamento estratégico', titleEn: 'Strategic planning', descPt: 'Análise e estruturação completa da operação.', descEn: 'Complete operation analysis and structuring.' },
    { id: 'consulting', icon: Users, titlePt: 'Acompanhamento consultivo próximo', titleEn: 'Close consultative follow-up', descPt: 'Parceria estratégica com foco em resultados.', descEn: 'Strategic partnership focused on results.' },
  ];
  return (
    <section className="bg-white py-24 md:py-28 border-t border-[#E6E6ED]">
      <div className="cv-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-8 bg-brass"></span>
              <span className="text-[11px] font-semibold uppercase tracking-[0.28em] text-abyssal">
                {t('DIFERENCIAIS', 'DIFFERENTIALS')}
              </span>
            </div>
            <h2 className="text-[32px] sm:text-4xl lg:text-[44px] tracking-tight font-bold text-abyssal mb-5">
              {t('Por que empresas escolhem a Costa Valente', 'Why companies choose Costa Valente')}
            </h2>
            <p className="text-lg text-steel leading-relaxed">
              {t(
                'Atendemos empresas que já operam no comércio exterior e buscam otimização, e empresas que desejam iniciar suas operações internacionais com segurança.',
                'We serve companies already operating in foreign trade seeking optimization, and companies looking to start international operations safely.'
              )}
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {diffs.map((d) => (
              <motion.div key={d.id} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                className="bg-white border border-[#E6E6ED] p-6 rounded-md" data-testid={`diff-${d.id}`}>
                <d.icon className="h-6 w-6 text-cobalt mb-3" strokeWidth={1.5} />
                <h4 className="text-base font-bold text-abyssal mb-1.5">{t(d.titlePt, d.titleEn)}</h4>
                <p className="text-sm text-steel leading-relaxed">{t(d.descPt, d.descEn)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- CTA FINAL — GRADIENTE oficial ---------- */
const CTASection = ({ t }) => (
  <section className="cv-cta-gradiente relative" data-testid="cta-section">
    <div className="absolute inset-0 bg-abyssal/45" aria-hidden="true"></div>
    <div className="relative cv-container py-20 md:py-24">
      <div className="max-w-2xl">
        <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-8 bg-brass"></span>
            <span className="text-[11px] font-semibold uppercase tracking-[0.28em] text-white">
              {t('FALE COM UM ESPECIALISTA', 'TALK TO A SPECIALIST')}
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-[40px] tracking-tight font-bold text-white mb-5 leading-[1.1]">
            {t('Vamos estruturar sua próxima importação?', "Let's structure your next import?")}
          </h2>
          <p className="text-lg text-white/85 mb-8 leading-relaxed">
            {t(
              'Converse com nossa equipe para avaliar o melhor modelo e conduzir a operação com segurança, eficiência e previsibilidade.',
              'Talk to our team to evaluate the best model and conduct the operation with security, efficiency and predictability.'
            )}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <a href="https://wa.me/5527996482098" target="_blank" rel="noopener noreferrer" data-testid="cta-whatsapp"
              className="inline-flex items-center justify-center gap-2 bg-white text-abyssal px-7 py-3.5 rounded-md font-semibold hover:bg-mist transition-colors">
              <Phone className="h-5 w-5" />
              {t('Falar com especialista', 'Talk to specialist')}
            </a>
            <Link to="/contato" data-testid="cta-contact"
              className="inline-flex items-center justify-center gap-2 bg-cobalt hover:bg-white hover:text-abyssal text-white border border-white/30 px-7 py-3.5 rounded-md font-semibold transition-colors">
              {t('Enviar mensagem', 'Send message')}
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);

const HomePage = () => {
  const { t } = useLanguage();
  return (
    <div className="min-h-screen bg-white">
      <HeroSection t={t} />
      <PillarsSection t={t} />
      <AboutSection t={t} />
      <ServicesSection t={t} />
      <SectorsSection t={t} />
      <DifferentialsSection t={t} />
      <CTASection t={t} />
    </div>
  );
};

export default HomePage;
